import type { Watchlist, Position } from "@foundation/watchlist-client";
import type {
  CreatePortfolio,
  PortfolioFlatHolding,
  PortfolioForm,
  PortfolioFormHolding,
  UpdatePortfolio,
} from "~types/portfolio";

export function prepareFormData(portfolio: Watchlist) {
  const { currency, id = -1, isDefault, name } = portfolio.portInfo;

  const holdings = portfolio.positions.map((position: Position) => {
    let totalShares = 0;
    let costBasis = 0;
    const lots = position.lots.map((lot) => {
      totalShares += lot.shares.number;
      costBasis += lot.shares.number * lot.shares.buyprice;

      return {
        lotId: lot.lotId,
        buyDate: lot.shares.buydate,
        shares: lot.shares.number.toString(),
        buyPrice: lot.shares.buyprice.toString(),
      };
    });

    // to remove trialing zeros
    totalShares = Number.isInteger(totalShares)
      ? totalShares
      : parseFloat(totalShares.toFixed(4));
    costBasis = parseFloat(costBasis.toFixed(4));

    return {
      ticker: position.security.ticker,
      companyName: position.security.LONG_COMP_NAME,
      totalShares,
      costBasis,
      lots,
    };
  });

  return { id, currency, holdings, isDefault, name };
}

export function prepareFlatHoldingData(
  holdings: Array<PortfolioFormHolding>
): Array<PortfolioFlatHolding> {
  return holdings.reduce(
    (result: Array<PortfolioFlatHolding>, { ticker, lots }, index) => {
      const subset: Array<PortfolioFlatHolding> = lots.map(
        ({ buyDate, buyPrice, shares }) => ({
          security: ticker,
          buydate: buyDate,
          shares: Number(shares) || 0,
          buyprice: Number(buyPrice) || 0,
          torder: index,
        })
      );

      return result.concat(subset);
    },
    []
  );
}

export function prepareUpdatePortfolioData(
  data: PortfolioForm
): UpdatePortfolio {
  const { name, currency, isDefault, holdings } = data;

  return {
    updatedAt: Date.now(),
    isChangedPortOverride: true,
    portChanges: {
      name,
      currency,
      isDefault,
    },
    holdingChanges: {
      isDeleteAllHoldingsPriorToUpdate: true,
      adds: prepareFlatHoldingData(holdings),
    },
  };
}

export function prepareCreatePortfolioData(
  data: PortfolioForm
): CreatePortfolio {
  const { name, currency, isDefault, holdings } = data;

  return {
    portMeta: {
      name,
      currency,
      isDefault,
    },
    holdings: prepareFlatHoldingData(holdings),
  };
}
